// src/Userdashboard/Userdashboard.js
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useLayoutEffect,
} from "react";
import axios from "axios";
import {
  LineChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  BarChart,
  Bar,
} from "recharts";
import "./Userdashboard.css";
import config from "../config";

/* ================== Consts ================== */
const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const ORANGE = "#ff9100ff";
const SELECTED_DEVICE_KEY = "selectedDeviceForReport";

/* ================== Helpers ================== */
const prettyMachineName = (name) => {
  const s = String(name || "").toLowerCase();
  if (
    s.includes("flowcontrol") ||
    s.includes("flow control") ||
    s.includes("flow veyor") ||
    s.includes("flow vayor")
  )
    return "Flow control";
  return name || "Machine";
};

const isFlowControlType = (name) => {
  const s = String(name || "").toLowerCase();
  return (
    s.includes("flowcontrol") ||
    s.includes("flow control") ||
    s.includes("flow veyor") ||
    s.includes("flow vayor")
  );
};

const fmtTime = (tMs) =>
  tMs
    ? new Date(tMs).toLocaleTimeString("en-IN", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
    : "-";

/* --- d/m/y | hh:mm (12-hour) for tooltip labels --- */
const formatDMYPipeHM = (val) => {
  try {
    const d = new Date(val);
    if (isNaN(d)) return String(val ?? "");
    const dd = d.getDate();
    const mm = d.getMonth() + 1;
    const yyyy = d.getFullYear();
    const date = `${dd}/${mm}/${yyyy}`;
    const time = d.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return `${date} | ${time}`;
  } catch {
    return String(val ?? "");
  }
};

/* robust pickers */
const num = (v) =>
  v === 0 || (typeof v === "number" && !isNaN(v)) ? v : Number(v);

const pick = (obj, path) => {
  const parts = path.split(".");
  let cur = obj;
  for (const p of parts) cur = cur?.[p];
  return cur;
};

const pickNum = (row, keys) => {
  for (const k of keys) {
    const v = pick(row, k);
    const n = num(v);
    if (n === 0 || (typeof n === "number" && !isNaN(n))) return n;
  }
  return undefined;
};

const toMs = (v) => {
  if (v == null) return undefined;
  if (typeof v === "number") return v < 1e12 ? v * 1000 : v; // seconds -> ms
  const p = Date.parse(v);
  return isNaN(p) ? undefined : p;
};

const pickTimeMs = (row, keys = ["timestamp", "time", "ts", "createdAt"]) => {
  for (const k of keys) {
    const v = pick(row, k);
    const t = toMs(v);
    if (t) return t;
  }
  return undefined;
};

/* units + clamps */
const kgOrDash = (v) =>
  typeof v === "number" && isFinite(v) ? `${v} kg` : "-";

const clampNonNeg = (v) =>
  typeof v === "number" && isFinite(v) ? Math.max(0, v) : undefined;

/* ---- NEW: check whether a timestamp is "today" (local) ---- */
const isSameCalendarDay = (tsMs, baseMs = Date.now()) => {
  if (!tsMs || !Number.isFinite(tsMs)) return false;
  const d = new Date(tsMs);
  const b = new Date(baseMs);
  return (
    d.getFullYear() === b.getFullYear() &&
    d.getMonth() === b.getMonth() &&
    d.getDate() === b.getDate()
  );
};
const daysAgoText = (tsMs) => {
  if (!tsMs || !Number.isFinite(tsMs)) return "-";

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const thatDay = new Date(tsMs);
  thatDay.setHours(0, 0, 0, 0);

  const diffDays = Math.floor(
    (today - thatDay) / (1000 * 60 * 60 * 24)
  );

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  return `${diffDays} days ago`;
};

// ⏱️ Decide machine status with idle logic
const getFinalMachineStatus = (rawStatus, timestamp) => {
  if (!timestamp) return "Idle";

  const now = Date.now();
  const diffMinutes = (now - timestamp) / (1000 * 60);

  // If data is not today → Idle
  if (!isSameCalendarDay(timestamp)) {
    return "Idle";
  }

  // If no update for 5+ minutes → Idle
  if (diffMinutes > 5) {
    return "Idle";
  }

  // Else use backend status
  if (
    String(rawStatus || "")
      .toLowerCase()
      .includes("run")
  ) {
    return "Running";
  }

  if (
    String(rawStatus || "")
      .toLowerCase()
      .includes("stop")
  ) {
    return "Stopped";
  }

  return "Idle";
};


/* ================== Component ================== */
const UserDashboard = () => {
  const [userData, setUserData] = useState(null);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [deviceData, setDeviceData] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [viewType, setViewType] = useState("daily");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [notLoggedIn, setNotLoggedIn] = useState(false);
  const [grandTotal, setGrandTotal] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().getMonth() + 1
  );
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Per-device latest stats (from polling endpoints)
  const [deviceStats, setDeviceStats] = useState({});
  // KPI above graph: only from /live/last/data for selected device
  const [latestTotalWeightKPI, setLatestTotalWeightKPI] = useState(undefined);
  const [latestFlowRateKPI, setLatestFlowRateKPI] = useState(undefined);

  const [loadingDaily, setLoadingDaily] = useState(false);
  const [loadingMonthly, setLoadingMonthly] = useState(false);
  const [loadingYearly, setLoadingYearly] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const LoaderOverlay = () => <div className="loader-overlay">Loading…</div>;

  /* ---------- read array safely ---------- */
  const getArr = (obj, path) => {
    if (!obj) return undefined;
    const parts = path.split(".");
    let cur = obj;
    for (const k of parts) {
      if (cur && cur[k] != null) cur = cur[k];
      else return undefined;
    }
    return Array.isArray(cur) ? cur : undefined;
  };

  /* ---------------- Load user + devices ---------------- */
  useEffect(() => {
  const stored = sessionStorage.getItem("userData");
  if (!stored) {
    setNotLoggedIn(true);
    return;
  }

  try {
    const parsed = JSON.parse(stored);

    const flow = Array.isArray(parsed.flowdevices)
      ? parsed.flowdevices
      : [];
    const packing = Array.isArray(parsed.packingdevices)
      ? parsed.packingdevices
      : [];

    const devices = [];

    // FLOW DEVICES (UNCHANGED)
    flow.forEach((d) => {
      const id = Number(d.deviceId ?? d.deviceIds);
      if (!id) return;

      devices.push({
        deviceId: id,
        deviceType: "FLOW",
        deviceName: d.deviceName || String(id),
        machineName: d.machineName || "Flow Control",
      });
    });

    // PACKING DEVICES ✅ MODIFIED
    // PACKING DEVICES ✅ FIXED WITH ALL FIELDS
packing.forEach((d) => {
  const id = Number(d.deviceId ?? d.deviceIds);
  if (!id) return;

  devices.push({
    deviceId: id,
    deviceType: "PACKING",
    deviceName: d.deviceName || String(id),
    machineName: d.machineName || "Packing Machine",

    _packingLive: {
      timestamp: d.timestamp
        ? new Date(d.timestamp).getTime()
        : undefined,

      // ✅ REQUIRED FOR UI
      batchId: d.batchId || d.batchName || "-",
      setWeight: d.bagWeightSet,
      actualWeight:
        typeof d.actualWeight === "number"
          ? d.actualWeight
          : undefined,
      bagCount: d.bagCount,
      accumulatedWeight: d.accumulatedWeight,
      scaleUsed: d.scaleUsed || "-",   // 🔴 FIX
      machineStatus: d.machineStatus,
    },
  });
});

    setUserData({
      userId: parsed.userId,
      userKey: parsed.userKey,
      companyName: parsed.companyName,
      devices,
    });

    // Auto-select if only one device
    if (devices.length === 1) {
      setSelectedDeviceId(devices[0].deviceId);
    }
  } catch (err) {
    console.error("User data parse error:", err);
    setNotLoggedIn(true);
  }
}, []);

// 🔹 Load last data for ALL device cards on page load

  /* ---------------- Small utils ---------------- */
  const getDaysInMonth = (y, m1) => new Date(y, m1, 0).getDate();

  const getNormalizedData = (data) => {
    if (!data || data.length === 0) return [];
    const sorted = [...data].sort((a, b) => a.timestamp - b.timestamp);
    const start = new Date(sorted[0].timestamp);
    start.setHours(0, 0, 0, 0);
    return [
      {
        ...sorted[0],
        timestamp: start.getTime(),
        totalWeight: 0,
        _rawTotalWeight: 0,
        setWeight: 0,
        actualWeight: 0,
        machineRunning: false,
        batchName: sorted[0].batchName || "",
      },
      ...sorted,
    ];
  };

  const getXTicks = () => {
    const ticks = [];
    const base = new Date();
    base.setHours(0, 0, 0, 0);
    for (let h = 0; h <= 24; h += 2) {
      const t = new Date(base);
      t.setHours(h);
      ticks.push(t.getTime());
    }
    return ticks;
  };

  const formatTimeTick = (time) =>
    new Date(time).toLocaleTimeString("en-IN", {
      hour: "numeric",
      hour12: true,
    });

  const parseDayFromLabel = (label, y, m1) => {
    if (label == null) return null;
    const s = String(label).trim();
    const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (iso) return Number(iso[3]);
    const dt = new Date(s);
    if (
      !isNaN(dt.valueOf()) &&
      dt.getFullYear() === y &&
      dt.getMonth() + 1 === m1
    )
      return dt.getDate();
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  };

  const expandMonthlyToAllDates = (summary, y, m1) => {
    const dim = getDaysInMonth(y, m1),
      map = new Map();
    (summary || []).forEach((it) => {
      const d = parseDayFromLabel(it.label, y, m1);
      const w = Number(it.totalWeight) || 0;
      if (d && d >= 1 && d <= dim) map.set(d, (map.get(d) || 0) + w);
    });
    const out = [];
    for (let d = 1; d <= dim; d++)
      out.push({ label: String(d), totalWeight: map.get(d) || 0 });
    return out;
  };

  const expandYearTo12Months = (summary) => {
    const map = new Map();
    (summary || []).forEach((it) => {
      const raw = String(it.label || "").trim();
      let idx = MONTH_NAMES.findIndex(
        (m) => m.toLowerCase() === raw.toLowerCase().slice(0, 3)
      );
      if (idx === -1) {
        const n = Number(raw);
        if (Number.isFinite(n) && n >= 1 && n <= 12) idx = n - 1;
      }
      if (idx >= 0)
        map.set(idx, (map.get(idx) || 0) + (Number(it.totalWeight) || 0));
    });
    const out = [];
    for (let i = 0; i < 12; i++)
      out.push({ label: MONTH_NAMES[i], totalWeight: map.get(i) || 0 });
    return out;
  };

  const toTonsSeries = (arr) =>
    (arr || []).map((x) => ({
      ...x,
      totalWeight: (Number(x.totalWeight) || 0) / 1000,
    }));

  /* -------- Horizontal scroll (DAILY) -------- */
  const [dailyWidth, setDailyWidth] = useState(1200);
  const scrollXRef = useRef(null);
  const [containerW, setContainerW] = useState(1200);
  const outerChartWrapRef = useRef(null);

  useLayoutEffect(() => {
    const measure = () => {
      const el = outerChartWrapRef.current;
      if (!el) return;
      const w = el.getBoundingClientRect().width || 1200;
      setContainerW(Math.max(320, Math.floor(w)));
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (outerChartWrapRef.current) ro.observe(outerChartWrapRef.current);
    return () => ro.disconnect();
  }, []); 

  const calcDailyWidth = (data) => {
    const n = Array.isArray(data) ? data.length : 0;
    const PX_PER_POINT = 80;
    const MIN = 1200;
    const MAX = 20000;
    return Math.min(MAX, Math.max(MIN, n * PX_PER_POINT));
  };

  const getXTicksDynamic = (w) => {
    const base = new Date();
    base.setHours(0, 0, 0, 0);
    const step = w > 3200 ? 1 : 2;
    const ticks = [];
    for (let h = 0; h <= 24; h += step) {
      const t = new Date(base);
      t.setHours(h);
      ticks.push(t.getTime());
    }
    return ticks;
  };

  // IMPORTANT CHANGE:
  // We NO LONGER auto-scroll to the end on every refresh.
  // So whatever position user is at in scrollX, it stays there.
  useEffect(() => {
    if (viewType !== "daily") return;
    const natural = calcDailyWidth(deviceData);
    const w = Math.max(containerW, natural, 800);
    setDailyWidth(w);
  }, [deviceData, containerW, viewType]);

  /* ------------------------------ Data fetchers ------------------------------ */
  const fetchDeviceData = useCallback(
    async (deviceIdParam) => {
      const id = Number(deviceIdParam || selectedDeviceId);
      if (!id) return;

      setLoadingDaily(true);
      try {
        const res = await axios.post(`${config.BASE_URL}/live/graph/data`, {
          deviceId: id,
          type: "daily",
        });
        const rows =
          (Array.isArray(res?.data) && res.data) ||
          (Array.isArray(res?.data?.data) && res.data.data) ||
          (Array.isArray(res?.data?.list) && res.data.list) ||
          (Array.isArray(res?.data?.rows) && res.data.rows) ||
          [];

        if (rows.length === 0) {
          setDeviceData([]);
          setLastUpdated(new Date());
          setDeviceStats((p) => ({
            ...p,
            [id]: {
              ...p[id],
              flowrate: undefined,
              totalWeight: undefined,
              totalWeightRaw: undefined,
              setWeight: undefined,
              batchName: undefined,
              machineName: p[id]?.machineName,
              machineStatusRaw: p[id]?.machineStatusRaw,
              timestamp: undefined,
            },
          }));
          return;
        }

        const data = rows.map((row) => {
          const rawStatus = pick(row, "status");
          let machineStatusRaw;
          if (rawStatus === 1 || rawStatus === "1" || rawStatus === true)
            machineStatusRaw = "Running";
          else if (rawStatus === 0 || rawStatus === "0" || rawStatus === false)
            machineStatusRaw = "Stopped";
          else {
            const alt = pick(row, "machineStatus") ?? pick(row, "statusText");
            machineStatusRaw = alt == null ? undefined : String(alt);
          }

          const tsMs = pickTimeMs(row);
          const flow = pickNum(row, [
            "flowRate",
            "flowrate",
            "flow",
            "currentFlow",
            "live.flowRate",
            "live.flowrate",
          ]);
          const setW = pickNum(row, [
            "setWeight",
            "bagWeightSet",
            "live.setWeight",
          ]);
          const totalWeight = pickNum(row, [
            "totalWeight",
            "accumulatedWeight",
            "live.totalWeight",
          ]);
          const rawTotal = typeof totalWeight === "number" ? totalWeight : undefined;
          const total = clampNonNeg(totalWeight);

          const isRealTs = !!tsMs;

          return {
            deviceId: row.deviceId,
            machinetype: prettyMachineName(
              row.machinetype || row.machineType || ""
            ),
            machineType: prettyMachineName(
              row.machinetype || row.machineType || ""
            ),
            timestamp: tsMs || Date.now(),
            _tsReal: isRealTs,
            batchName: row.batchName || "",
            machineStatusRaw,
            flowrate: typeof flow === "number" ? flow : undefined,
            setWeight: typeof setW === "number" ? setW : undefined,
            totalWeight: total, // chart value (>=0)
            _rawTotalWeight: rawTotal, // exact backend
            actualWeight: 0,
          };
        });

        // Seed 00:00 with 0kg
        const start = new Date();
        start.setHours(0, 0, 0, 0);
        const startMs = start.getTime();
        data.sort((a, b) => a.timestamp - b.timestamp);
        if (data[0].timestamp > startMs) {
          data.unshift({
            deviceId: id,
            timestamp: startMs,
            _tsReal: false,
            machinetype: data[0].machinetype,
            machineType: data[0].machineType,
            totalWeight: 0,
            _rawTotalWeight: 0,
            setWeight: 0,
            actualWeight: 0,
            flowrate: 0,
            machineStatusRaw: "Stopped",
            batchName: data[0].batchName || "",
          });
        }
 
        setDeviceData(data);
        setLastUpdated(new Date());

        const last = data[data.length - 1];
        setDeviceStats((prev) => ({
          ...prev,
          [id]: {
            flowrate:
              typeof last.flowrate === "number" ? last.flowrate : undefined,
            totalWeight: Math.max(0, Number(last.totalWeight) || 0),
            totalWeightRaw:
              typeof last._rawTotalWeight === "number"
                ? last._rawTotalWeight
                : undefined,
            setWeight: last.setWeight,
            batchName: last.batchName,
            machineName: last.machinetype || last.machineType,
            machineStatusRaw: last.machineStatusRaw,
            timestamp: last._tsReal
              ? last.timestamp
              : data
                  .slice()
                  .reverse()
                  .find((r) => r._tsReal)?.timestamp ?? last.timestamp,
          },
        }));
      } catch (e) {
        console.error(e);
        setDeviceData([]);
      } finally {
        setLoadingDaily(false);
      }
    },
    [selectedDeviceId]
  );

// Fill card stats for ALL devices (cards grid) – uses /live/last/data
const fetchLatestForAllDevices = useCallback(
  async (devices) => {
    if (!devices?.length) return;

    const limit = 3; // parallel request limit
    let i = 0;
    const results = {};

    const runBatch = async () => {
      const slice = devices.slice(i, i + limit);
      i += limit;

      await Promise.allSettled(
        slice.map(async (d) => {
          const id = Number(d.deviceId);
          if (!id) return;

          try {
            const res = await axios.post(
              `${config.BASE_URL}/live/last/data`,
              { deviceId: id }
            );

            // Backend format: { deviceId, data: [...] }
            const rec = Array.isArray(res?.data?.data)
              ? res.data.data[0]
              : null;

            if (!rec) {
              results[id] = {
                flowrate: undefined,
                totalWeight: undefined,
                totalWeightRaw: undefined,
                setWeight: undefined,
                batchName: undefined,
                machineName: d.machineName,
                machineStatusRaw: undefined,
                timestamp: undefined,
              };
              return;
            }

            // ---- timestamp (keep even if old) ----
            const ts = pickTimeMs(rec, [
              "timestamp",
              "time",
              "ts",
              "createdAt",
            ]);

            // ---- numeric values (always normalize) ----
            const flow = Number(rec.flowrate ?? rec.flowRate ?? undefined);
            const setW = Number(rec.setWeight ?? rec.bagWeightSet ?? undefined);

            const totalWeightRaw = Number(
              rec.totalWeight ??
                rec.accumulatedWeight ??
                rec.total ??
                undefined
            );

            const totalWeight =
              Number.isFinite(totalWeightRaw) && totalWeightRaw >= 0
                ? totalWeightRaw
                : undefined;

            // ---- machine status ----
            const machineStatusRaw =
              rec.status === 1 || rec.status === "1" || rec.status === true
                ? "Running"
                : rec.status === 0 || rec.status === "0" || rec.status === false
                ? "Stopped"
                : "-";

            const batchName = rec.batchName || undefined;

            const machineName = prettyMachineName(
              rec.machinetype || rec.machineName || d.machineName
            );

            // ✅ ALWAYS store last row for card (NO DATE CHECK)
results[id] = {
  flowrate: Number.isFinite(flow) ? flow : undefined,

  totalWeight: Number.isFinite(totalWeight)
    ? totalWeight
    : undefined,

  totalWeightRaw: Number.isFinite(totalWeightRaw)
    ? totalWeightRaw
    : undefined,

  accumulatedWeight: Number.isFinite(rec.accumulatedWeight)
    ? rec.accumulatedWeight
    : undefined,

  setWeight: Number.isFinite(setW)
    ? setW
    : undefined,

  bagCount: Number.isFinite(rec.bagCount)
    ? rec.bagCount
    : undefined,

  batchName,
  machineName,
  machineStatusRaw,
  timestamp: ts,
};
          } catch (err) {
            console.error(
              "fetchLatestForAllDevices error for",
              d.deviceId,
              err
            );

            results[id] = {
              flowrate: undefined,
              totalWeight: undefined,
              totalWeightRaw: undefined,
              setWeight: undefined,
              batchName: undefined,
              machineName: d.machineName,
              machineStatusRaw: undefined,
              timestamp: undefined,
            };
          }
        })
      );

      if (i < devices.length) {
        await runBatch();
      }
    };

    await runBatch();

    setDeviceStats((prev) => ({
      ...prev,
      ...results,
    }));
  },
  []
);
useEffect(() => {
  if (!userData?.devices?.length) return;

  fetchLatestForAllDevices(userData.devices);
}, [userData, fetchLatestForAllDevices]);

useEffect(() => {
  if (!userData?.devices?.length) return;

  const interval = setInterval(() => {
    fetchLatestForAllDevices(userData.devices);
  }, 60000); // ⏱️ 1 minute

  return () => clearInterval(interval);
}, [userData, fetchLatestForAllDevices]);

/* -------- KPI from /live/last/data for selected device --- */
const fetchLatestTotalWeightKPI = useCallback(
  async (deviceIdParam) => {
    const id = Number(deviceIdParam || selectedDeviceId);
    if (!id) return;

    try {
      const res = await axios.post(
        `${config.BASE_URL}/live/last/data`,
        { deviceId: id }
      );

      // Backend: { deviceId, data: [...] }
      const rec = Array.isArray(res?.data?.data)
        ? res.data.data[0]
        : null;

      if (!rec) {
        setLatestTotalWeightKPI(undefined);
        setLatestFlowRateKPI(undefined);
        return;
      }

      // ---- timestamp (keep for "last updated") ----
      const ts = pickTimeMs(rec, [
        "timestamp",
        "time",
        "ts",
        "createdAt",
      ]);

      // ---- values (ALWAYS use last row) ----
      const totalWeight = Number(rec.totalWeight);
      const flowRate = Number(rec.flowrate ?? rec.flowRate);

      setLatestTotalWeightKPI(
        Number.isFinite(totalWeight) ? totalWeight : undefined
      );

      setLatestFlowRateKPI(
        Number.isFinite(flowRate) ? flowRate : undefined
      );

      // (optional: if you want last updated time for KPI)
      // setLatestKPITimestamp(ts);

    } catch (err) {
      console.error("KPI fetch failed:", err);
      setLatestTotalWeightKPI(undefined);
      setLatestFlowRateKPI(undefined);
    }
  },
  [selectedDeviceId]
);
  const isSelectedDataToday = isSameCalendarDay(
  deviceStats[selectedDeviceId]?.timestamp
);
useEffect(() => {
  if (!selectedDeviceId) return;

  // Always fetch once
  fetchLatestTotalWeightKPI(selectedDeviceId);

  // Auto refresh ONLY if today
  if (!isSelectedDataToday) return;

  const t = setInterval(() => {
    fetchLatestTotalWeightKPI(selectedDeviceId);
  }, 60000);

  return () => clearInterval(t);
}, [
  selectedDeviceId,
  isSelectedDataToday,
  fetchLatestTotalWeightKPI,
]);

useEffect(() => {
  if (!selectedDeviceId) return;
  if (viewType !== "daily") return;
  if (!isSelectedDataToday) return;

  // find selected device
  const selectedDevice = userData?.devices?.find(
    (d) => Number(d.deviceId) === Number(selectedDeviceId)
  );

  if (!selectedDevice) return;

  // ✅ ONLY FLOW DEVICES SHOULD POLL
  if (selectedDevice.deviceType !== "FLOW") {
    return;
  }

  // initial load
  fetchDeviceData(selectedDeviceId);

  const interval = setInterval(() => {
    fetchDeviceData(selectedDeviceId);
  }, 60000); // ⏱️ 1 minute

  return () => clearInterval(interval);
}, [
  selectedDeviceId,
  viewType,
  isSelectedDataToday,
  fetchDeviceData,
  userData,
]);
  /* -------------------------- Monthly / Yearly / Hist ------------------------ */
  const fetchMonthlyData = async () => {
    if (!selectedDeviceId) return;
    setLoadingMonthly(true);
    try {
      const { data } = await axios.post(
        `${config.BASE_URL}/live/graph/data`,
        {
          deviceId: Number(selectedDeviceId),
          month: Number(selectedMonth),
          year: Number(selectedYear),
          type: "monthly",
        }
      );
      const r = data || {};
      const summary = r.summary || r.data || r.rows || [];
      const kgSeries = (summary || []).map((i) => ({
        label: i.label,
        totalWeight: Number(i.totalWeight) || 0,
      }));
      const dim = getDaysInMonth(Number(selectedYear), Number(selectedMonth));
      const map = new Map();
      (kgSeries || []).forEach((it) => {
        const s = String(it.label || "").trim();
        const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
        const d = iso ? Number(iso[3]) : Number(s);
        const w = Number(it.totalWeight) || 0;
        if (Number.isFinite(d) && d >= 1 && d <= dim)
          map.set(d, (map.get(d) || 0) + w);
      });
      const out = [];
      for (let d = 1; d <= dim; d++)
        out.push({ label: String(d), totalWeight: map.get(d) || 0 });
      setSummaryData(out);
      setGrandTotal(Number(r.grandTotal) || 0);
      setViewType("monthly");
    } catch {
      setSummaryData([]);
      setGrandTotal(0);
    } finally {
      setLoadingMonthly(false);
    }
  };

  const fetchYearlyData = async () => {
    if (!selectedDeviceId) return;
    setLoadingYearly(true);
    try {
      const { data } = await axios.post(
        `${config.BASE_URL}/live/graph/data`,
        {
          deviceId: Number(selectedDeviceId),
          year: Number(selectedYear),
          type: "yearly",
        }
      );
      const r = data || {};
      const summary = r.summary || r.data || r.rows || [];
      const kgSeries = (summary || []).map((i) => ({
        label: i.label,
        totalWeight: Number(i.totalWeight) || 0,
      }));
      const out = expandYearTo12Months(kgSeries);
      setSummaryData(out);
      setGrandTotal(Number(r.grandTotal) || 0);
      setViewType("yearly");
    } catch {
      setSummaryData([]);
      setGrandTotal(0);
    } finally {
      setLoadingYearly(false);
    }
  };

  const fetchHistoryData = async () => {
    if (!selectedDeviceId) return;
    setLoadingHistory(true);
    try {
      const { data } = await axios.post(
        `${config.BASE_URL}/live/graph/data`,
        {
          deviceId: Number(selectedDeviceId),
          allYearHistory: true,
        }
      );
      const r = data || {};
      const summary = r.summary || r.data || r.rows || [];
      const kgSeries = (summary || []).map((i) => ({
        label: String(i.label),
        totalWeight: Number(i.totalWeight) || 0,
      }));
      setSummaryData(kgSeries);
      setGrandTotal(Number(r.grandTotal) || 0);
      setViewType("history");
    } catch {
      setSummaryData([]);
      setGrandTotal(0);
    } finally {
      setLoadingHistory(false);
    }
  };

  /* ------------------------- Selection refresh handler ----------------------- */
  const handleRefreshSelection = () => {
    setSelectedDeviceId("");
    setViewType("daily");
    setSummaryData([]);
    setGrandTotal(0);
    setDeviceData([]);
    setLatestTotalWeightKPI(undefined);
  };

  /* ---------------------------- Card select handler -------------------------- */
  const handleSelectDevice = (d) => {
    const id = Number(d.deviceId);
    setSelectedDeviceId(id);
    setViewType("daily");
    setSummaryData([]);
    setGrandTotal(0); 
    setDeviceData([]);
    localStorage.setItem(
      "selectedDeviceForReport",
      JSON.stringify({
        deviceId: id,
        deviceName: d.deviceName || String(id),
        machinetype: d.machineName || d.machinetype || "",
      })
      
    );
    try {
      sessionStorage.setItem(
        SELECTED_DEVICE_KEY,
        JSON.stringify({
          deviceId: id,
          deviceName: d.deviceName || String(id),
          machinetype: d.machineName || d.machinetype || "",
          savedAt: Date.now(),
          
        })
      );
    } catch {}
    console.log("Selected:", d.deviceId, d.deviceType);
    setLatestTotalWeightKPI(undefined);
    fetchDeviceData(id);
    fetchLatestTotalWeightKPI(id);
  };

  /* ------------------------ Device Card ------------------- */
  const renderCard = (d, idx, asSelected = false) => {
  const variants = ["is-red", "is-amber", "is-green", "is-blue"];
  const variant = variants[idx % variants.length];
  const isActive = Number(selectedDeviceId) === Number(d.deviceId);
  const isSelectedCard = asSelected && isActive;
  const isPacking = d.deviceType === "PACKING";

  // ---------- DEFAULT VALUES ----------
  let cardTime = "-";
  let cardFlowExact;
  let cardTotal;
  let cardSetW;
  let cardBatch = "-";

  // ---------- FLOW MACHINE (UNCHANGED LOGIC) ----------
  if (!isPacking) {
    const stats = deviceStats[d.deviceId] || {};

  const finalStatus = getFinalMachineStatus(
  stats.machineStatusRaw,
  stats.timestamp
);


    cardTime = fmtTime(stats.timestamp);

    cardFlowExact =
      typeof stats.flowrate === "number" ? stats.flowrate : undefined;

    const liveTotal =
      typeof stats.accumulatedWeight === "number"
        ? stats.accumulatedWeight
        : typeof stats.totalWeight === "number"
        ? stats.totalWeight
        : typeof stats.totalWeightRaw === "number"
        ? stats.totalWeightRaw
        : undefined;

    cardTotal = typeof liveTotal === "number" ? liveTotal : undefined;

    if (isSelectedCard && typeof latestTotalWeightKPI === "number") {
      cardTotal = latestTotalWeightKPI;
    }

    cardSetW =
      typeof stats.setWeight === "number" ? stats.setWeight : undefined;

    cardBatch = stats.batchName || "-";
  }

  // ---------- PACKING MACHINE (LOGIN DATA) ----------
  const packingLive = d._packingLive || {};

  const badgeName = prettyMachineName(d.machineName || d.machinetype);

  return (
    <div
      key={(asSelected ? "sel-" : "card-") + (d.deviceId || idx)}
      className={`device-card step-panel ${variant} ${
        isActive ? "active" : ""
      } ${asSelected ? "selected-card" : ""}`}
      onClick={() => !asSelected && handleSelectDevice(d)}
      role="button"
      tabIndex={0}
    >
      <span className="step-num">
        {String((idx % 4) + 1).padStart(2, "0")}
      </span>

      <div className="step-head">
        <h4 className="step-title">{d.deviceName || "Device"}</h4>
        <span className="step-badge">{badgeName}</span>
        {asSelected && (
          <button
            className="refresh-pill"
            title="Back to card selection"
            onClick={(e) => {
              e.stopPropagation();
              handleRefreshSelection();
            }}
          >
            ⟳ Back
          </button>
        )}
      </div>

      {/* ================= TIME ================= */}
      <p className="step-line">
        <span className="step-key">Time:</span>
        <span className="step-val">
          {isPacking
            ? packingLive.timestamp
              ? new Date(packingLive.timestamp).toLocaleString()
              : "-"
            : cardTime}

          {!isPacking && deviceStats[d.deviceId]?.timestamp && (
            <span className="days-ago">
              {" "}({daysAgoText(deviceStats[d.deviceId].timestamp)})
            </span>
          )}
        </span>
      </p>

      {/* ================= PACKING MACHINE CARD ================= */}
      {isPacking ? (
        <>
          <p className="step-line">
            <span className="step-key">Bag Weight Set:</span>
            <span className="step-val">
              {typeof packingLive.setWeight === "number"
                ? `${packingLive.setWeight} kg`
                : "-"}
            </span>
          </p>

          <p className="step-line">
            <span className="step-key">Actual Weight:</span>
            <span className="step-val">
              {typeof packingLive.actualWeight === "number"
                ? `${packingLive.actualWeight} kg`
                : "-"}
            </span>
          </p>

          <p className="step-line">
            <span className="step-key">Bag Count:</span>
            <span className="step-val">
              {typeof packingLive.bagCount === "number"
                ? packingLive.bagCount
                : 0}
            </span>
          </p>

          <p className="step-line">
            <span className="step-key">Accumulated Weight:</span>
            <span className="step-val">
              {typeof packingLive.accumulatedWeight === "number"
                ? `${packingLive.accumulatedWeight} kg`
                : "-"}
            </span>
          </p>

          <div className="step-line card-foot">
            <span className="step-key">Batch Name:</span>
            <span className="step-val">
              {packingLive.batchId || "-"}
            </span>
          </div>
        </>
      ) : (
        /* ================= FLOW MACHINE CARD (UNCHANGED) ================= */
        <>
          {/* <p className="step-line">
            <span className="step-key">Flow Rate:</span>
            <span className="step-val">
              {typeof cardFlowExact === "number"
                ? `${cardFlowExact.toLocaleString(undefined, {
                    maximumFractionDigits: 2,
                  })} t/h`
                : "-"}
            </span>
          </p> */}

          <p className="step-line">
            <span className="step-key">Total Weight:</span>
            <span className="step-val">
              {typeof cardTotal === "number" ? `${cardTotal} kg` : "-"}
            </span>
          </p>

          <p className="step-line">
            <span className="step-key">Set Weight:</span>
            <span className="step-val">
              {typeof cardSetW === "number" ? `${cardSetW} kg` : "-"}
            </span>
          </p>

          <div className="step-line card-foot">
            <span className="step-key">Batch:</span>
            <span className="step-val">{cardBatch}</span>
          </div>
        </>
      )}
    </div>
  );
};

  /* --------------------------------- Render --------------------------------- */
  if (notLoggedIn)
    return <p>User not logged in. Please go back to login page.</p>;
  if (!userData) return <p>Loading dashboard...</p>;

  const devices = userData.devices || [];
  const selectedDevice = devices.find(
    (d) => Number(d.deviceId) === Number(selectedDeviceId)
  );

  const lastData =
  selectedDevice?.deviceType === "PACKING"
    ? selectedDevice?._packingLive || {}
    : deviceData[deviceData.length - 1] ||
      deviceStats[selectedDeviceId] ||
      {};

    // 🔎 DEBUG: check packing live data
if (selectedDevice?.deviceType === "PACKING") {
  console.log("PACKING lastData:", lastData);
}


  const isChartLoading =
    viewType === "daily"
      ? loadingDaily
      : viewType === "monthly"
      ? loadingMonthly
      : viewType === "yearly"
      ? loadingYearly
      : loadingHistory;

  const selectedLastStatusRaw = getFinalMachineStatus(
  lastData.machineStatusRaw,
  lastData.timestamp
);


  const centerY = (() => {
    const vals = (deviceData || []).map((d) =>
      typeof d.totalWeight === "number" && isFinite(d.totalWeight)
        ? d.totalWeight
        : 0
    );
    const maxV = Math.max(0, ...vals);
    return maxV / 2;
  })();

  return (
    <div id="move1">
      <div className="device-list">
        <p className="device-list-title">
          <strong>Devices</strong>
        </p>

        {devices.length === 0 ? (
          <p>No devices found.</p>
        ) : (
          <>
            {selectedDeviceId ? (
              <div className="device-selected-slot">
                {renderCard(selectedDevice || devices[0], 0, true)}
              </div>
            ) : (
              <div className="device-grid">
                {devices.map((d, idx) => renderCard(d, idx))}
              </div>
            )}
          </>
        )}
      </div>

      {!selectedDeviceId && devices.length > 0 && (
        <p className="nodata">Select a device card to load data.</p>
      )}

      {selectedDeviceId &&  (
        <div className="section">
          <div className="panel" ref={outerChartWrapRef}>
            <div className="panel__head">
              <div>
                <div className="panel__title">
                  {selectedDevice
                    ? selectedDevice.deviceName || "Machine Summary"
                    : "Machine Summary"}
                </div>
                <div className="top-meta" style={{ margin: 0 }}>
                  <div className="top-left">
                    <span className="machine-type">
                      <strong>Machine Type:</strong>{" "}
                      {prettyMachineName(
                        lastData.machinetype ||
                          lastData.machineType ||
                          selectedDevice?.machineName ||
                          "Unknown"
                      )}
                    </span>
                    {(lastData.batchName || selectedDevice?.batchName) && (
                      <span className="batch-pill">
                        Batch: {lastData.batchName || selectedDevice?.batchName}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="panel__meta">
                <strong>Last Updated:</strong>{" "}
                {lastUpdated ? lastUpdated.toLocaleTimeString() : "-"}
              </div>
            </div>

            <div className="panel__body">
              {selectedDevice?.deviceType === "FLOW" ? (
                <>
                  <div className="kpi-row">
                    <div className="kpi-chip">
                      Machine Status:&nbsp;
                      <span
                        style={{
                          color:
                                selectedLastStatusRaw === "Running"
                                  ? "green"
                                  : selectedLastStatusRaw === "Stopped"
                                  ? "red"
                                  : "orange",

                          fontWeight: 800,
                        }}
                      >
                        {selectedLastStatusRaw ?? "-"}
                      </span>
                    </div>
                    <div className="kpi-chip">
                      Timestamp:&nbsp;
                      {lastData.timestamp
                        ? new Date(lastData.timestamp).toLocaleString()
                        : "-"}
                    </div>
                    <div className="kpi-chip">
                      Set Weight:&nbsp;
                      {kgOrDash(lastData.setWeight)}
                    </div>
                    <div className="kpi-chip">
                      Total Weight:&nbsp;
                      {typeof latestTotalWeightKPI === "number"
                        ? `${latestTotalWeightKPI} kg`
                        : kgOrDash(lastData.totalWeight)}
                    </div>
                  </div>

                  <div
                    className="inline-tabs"
                    style={{ flexWrap: "wrap", gap: 8 }}
                  >
                    {["daily", "monthly", "yearly", "history"].map((type) => (
                      <span
                        key={type}
                        onClick={() => {
                          setViewType(type);
                          if (type === "monthly") fetchMonthlyData();
                          else if (type === "yearly") fetchYearlyData();
                          else if (type === "history") fetchHistoryData();
                        }}
                        className={`view-pill ${
                          viewType === type ? "active" : ""
                        }`}
                      >
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </span>
                    ))}

                    {viewType === "monthly" && (
                      <div
                        className="month-year-controls"
                        style={{
                          display: "flex",
                          gap: 8,
                          alignItems: "center",
                          marginLeft: 12,
                        }}
                      >
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <span>Month</span>
                          <select
                            value={selectedMonth}
                            onChange={(e) =>
                              setSelectedMonth(Number(e.target.value))
                            }
                          >
                            {MONTH_NAMES.map((m, i) => (
                              <option key={m} value={i + 1}>
                                {m}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <span>Year</span>
                          <select
                            value={selectedYear}
                            onChange={(e) =>
                              setSelectedYear(Number(e.target.value))
                            }
                          >
                            {Array.from({ length: 6 }).map((_, k) => {
                              const y = new Date().getFullYear() - 4 + k;
                              return (
                                <option key={y} value={y}>
                                  {y}
                                </option>
                              );
                            })}
                          </select>
                        </label>
                        <button
                          className="refresh-pill"
                          onClick={fetchMonthlyData}
                        >
                          Load
                        </button>
                      </div>
                    )}

                    {viewType === "yearly" && (
                      <div
                        className="month-year-controls"
                        style={{
                          display: "flex",
                          gap: 8,
                          alignItems: "center",
                          marginLeft: 12,
                        }}
                      >
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <span>Year</span>
                          <select
                            value={selectedYear}
                            onChange={(e) =>
                              setSelectedYear(Number(e.target.value))
                            }
                          >
                            {Array.from({ length: 8 }).map((_, k) => {
                              const y = new Date().getFullYear() - 6 + k;
                              return (
                                <option key={y} value={y}>
                                  {y}
                                </option>
                              );
                            })}
                          </select>
                        </label>
                        <button
                          className="refresh-pill"
                          onClick={fetchYearlyData}
                        >
                          Load
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="data-chart-scroll">
  <div
    className="data-chart-container"
    style={{ position: "relative" }}
  >
    {isChartLoading && <LoaderOverlay />}

    {/* ================= DAILY GRAPH ================= */}
    {viewType === "daily" && isSelectedDataToday && (
      <div
        className="chart-scroll-x"
        ref={scrollXRef}
        style={{
          overflowX: "auto",
          overflowY: "hidden",
          width: "100%",
        }}
      >
        <div
          className="chart-inner"
          style={{
            width: dailyWidth,
            minWidth: dailyWidth,
            height: 400,
          }}
        >
          <ResponsiveContainer width="100%" height={400}>
            <LineChart
              data={getNormalizedData(deviceData)}
              width={dailyWidth}
              margin={{
                top: 20,
                right: 60,
                left: 60,
                bottom: 50,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="timestamp"
                type="number"
                scale="time"
                domain={[
                  new Date().setHours(0, 0, 0, 0),
                  new Date().setHours(24, 0, 0, 0),
                ]}
                ticks={getXTicksDynamic(dailyWidth)}
                tickFormatter={formatTimeTick}
                tickMargin={15}
                label={{
                  value: "Time",
                  position: "insideBottom",
                  dy: 30,
                  fontSize: 14,
                }}
              />
              <YAxis
                domain={[0, (max) => (max || 0) * 1.1]}
                tick={false}
                label={{
                  value: "kg",
                  angle: -90,
                  position: "insideLeft",
                  offset: 18,
                }}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    const raw = payload[0].value;
                    const pl = payload[0].payload || {};
                    const display =
                      typeof raw === "number" && isFinite(raw)
                        ? `${raw.toLocaleString()} kg`
                        : "-";
                    return (
                      <div className="tooltip-card">
                        <p className="t1">
                          {formatDMYPipeHM(label)}
                        </p>
                        <p className="t2">
                          Difference Total Weight: {display}
                        </p>
                        {pl.batchName && (
                          <p className="t3">
                            Batch Name: {pl.batchName}
                          </p>
                        )}
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <ReferenceLine
                y={0}
                stroke="grey"
                strokeDasharray="5 5"
              />
              <ReferenceLine
                y={centerY}
                stroke="#cccccc"
                strokeWidth={1.5}
                strokeDasharray="6 6"
              />
              <Area
                type="linear"
                dataKey="totalWeight"
                stroke={ORANGE}
                fill={ORANGE}
                strokeWidth={2}
                dot={{ r: 0 }}
                activeDot={{ r: 6 }}
              />
              <Line
                type="linear"
                dataKey="totalWeight"
                stroke={ORANGE}
                strokeWidth={2}
                dot={{ r: 0 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    )}

    {/* ===== DAILY GRAPH HIDDEN MESSAGE (PAST DATA) ===== */}
    {viewType === "daily" && !isSelectedDataToday && (
      <p className="nodata">
        Live graph hidden — last data is from{" "}
        {daysAgoText(deviceStats[selectedDeviceId]?.timestamp)}
      </p>
    )}

    {/* ========== MONTHLY / YEARLY / HISTORY ========== */}
    {viewType !== "daily" && (
      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={toTonsSeries(summaryData)}
          margin={{
            top: 20,
            right: 60,
            left: 60,
            bottom: 50,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 12 }}
            interval={0}
            label={{
              value:
                viewType === "monthly"
                  ? "Date"
                  : viewType === "yearly"
                  ? "Month"
                  : "Year",
              position: "insideBottom",
              offset: -5,
              fontSize: 14,
            }}
          />
          <YAxis
            domain={[0, (max) => (max || 0) * 1.1]}
            tickMargin={8}
            label={{
              value: "ton",
              angle: -90,
              position: "insideLeft",
              offset: 18,
            }}
            tickFormatter={(v) =>
              v.toLocaleString(undefined, {
                maximumFractionDigits: 2,
              })
            }
          />
          <Tooltip
            formatter={(v) =>
              `${Number(v).toLocaleString(undefined, {
                maximumFractionDigits: 2,
              })} ton`
            }
          />
          <Bar
            dataKey="totalWeight"
            fill={ORANGE}
            barSize={40}
          />
        </BarChart>
      </ResponsiveContainer>
    )}

    {(viewType === "monthly" ||
      viewType === "yearly" ||
      viewType === "history") && (
      <div className="grand-total">
        <strong>Grand Total:</strong>{" "}
        {(grandTotal / 1000).toLocaleString(undefined, {
          maximumFractionDigits: 2,
        })}{" "}
        ton
      </div>
    )}
  </div>
</div>
                </>
              ) : (
                <>
                  <div className="panel__title" style={{ marginBottom: 8 }}>
                    Batch ID: {lastData.batchId || "-"}
                  </div>
                  <div className="kpi-row">
                    <div className="kpi-chip">
                      Timestamp:&nbsp;
                      {lastData.timestamp
                        ? new Date(lastData.timestamp).toLocaleString()
                        : "-"}
                    </div>
                    <div className="kpi-chip">
                      Bag Weight Set:&nbsp;
                      {kgOrDash(lastData.setWeight)}
                    </div>
                    <div className="kpi-chip">
                      Actual Weight:&nbsp;
                      {typeof lastData.actualWeight === "number"
                        ? `${lastData.actualWeight} kg`
                        : "-"}
                    </div>
                    <div className="kpi-chip">
                      Bag Count:&nbsp;
                      {typeof lastData.bagCount === "number"
                        ? lastData.bagCount
                        : 0}
                    </div>
                    <div className="kpi-chip">
                      Accumulated Weight:&nbsp;
                      {typeof lastData.accumulatedWeight === "number"
                        ? `${lastData.accumulatedWeight} kg`
                        : "-"}
                    </div>
                    <div className="kpi-chip">
                      Scale Used:&nbsp;
                      {lastData.scaleUsed || "-"}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {selectedDeviceId && deviceData.length === 0 && (
        <p className="nodata">No data available for this device today.</p>
      )}
      {selectedDeviceId && !isSelectedDataToday && deviceData.length > 0 && (
        <p className="nodata">
          Live graph hidden — last data is from{" "}
          {daysAgoText(deviceStats[selectedDeviceId]?.timestamp)}.
        </p>
     )}
    </div>
  );
};

export default UserDashboard;  